import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import qs from 'qs';
import { priceToFixed } from '@/utils'
import * as apis from '@/apis';
import { Avatar, Tag, Tooltip } from 'antd';
import { transactionTypes, payMethodTypes } from '../constants';
import Filters from './filter';
import { orderPreview } from '@/models/orders/actions';
import styles from './transactions.module.scss';
import PageableTable from '@/components/PageableTable';

function useQuery(str) {
  return useMemo(() => qs.parse(str), [str]);
}


const columns = [
  {
    key: '_id',
    title: '',
    width: 30,
    render: (text, record, idx) => (
      <div style={{ whiteSpace: 'nowrap' }}>{idx + 1}.</div>
    ),
  },
  {
    title: '交易类型',
    render: (item) => {
      if (item.type === 'tenant/balance_recharge' && !item.isPaid) {
        return (
          <>
            {transactionTypes[item.type]}
            <Tag color="#87d068" style={{ marginLeft: 12 }}>
              待支付
            </Tag>
          </>
        );
      }

      return (
        <Tooltip
          title={[item.title, item.paid_memo].filter(Boolean).join(' - ')}
        >
          {transactionTypes[item.type] || `未知类型${item.type}`}
        </Tooltip>
      );
    },
  },
  {
    title: '交易金额',
    render: (item) => (
      <div>
        <span>¥{priceToFixed(item?.amount, 2) || 0}</span>
        <span>
          {payMethodTypes[item?.pay_method] || ''}
        </span>
      </div>
    ),
  },
  {
    title: '余额',
    render: (item) => (
      <div>
        <span>¥{priceToFixed(item?.balance, 2) || 0}</span>
      </div>
    ),
  },
  {
    title: '剩余信用',
    render: (item) => (
      <div>
        <span>{item.tenant ? ('¥' + item.credit?.toFixed(2)) : '-'}</span>
      </div>
    ),
  },
  {
    title: '工单',
    render: (item) =>
      item.order?.order_no ? (
        <span>
          {item.order.order_no}
          {item.order.payer_type === 2 && (
            <span style={{ paddingLeft: 5 }}>
              <Tag>保外</Tag>
            </span>
          )}
        </span>
      ) : (
        '-'
      ),
  },
  {
    title: '企业',
    render: (item) =>
      item.tenant ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {item.tenant.logo && <Avatar shape="square" src={item.tenant.logo} />}
          <span style={{ paddingLeft: 5 }}>{item.tenant.short_name}</span>
        </div>
      ) : (
        '-'
      ),
  },
  {
    title: '用户',
    render: (item) =>
    (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar shape="square" src={item?.user?.avatar || 'https://thirdwx.qlogo.cn/mmopen/vi_32/POgEwh4mIHO4nibH0KlMECNjjGxQUq24ZEaGT4poC6icRiccVGKSyXwibcPq4BWmiaIGuG1icwxaQX6grC9VemZoJ8rg/132'} />
        <span style={{ paddingLeft: 5 }}>{item?.user?.name ? item.user.name : (item.user?.roles?.worker ? item.user?.roles?.worker.realname : '-')}</span>
      </div>
    ),
  },
  {
    title: '交易时间',
    render: (item) => dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
  },
];

// 所有 交易记录
export default function Transactions(props) {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery(location.search.slice(1));
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const limit = 10;
  const dispatch = useDispatch();
  const pagination = {
    showTotal: (total) => `共计 ${total}条 记录`,
    showQuickJumper: true,
  };
  const handleRowClick = useCallback((item) => () => {
    dispatch(
      orderPreview({
        title: `工单 - ${item.order_no}`,
        id: item._id,
      })
    );
  });

  const fetch = useCallback(() => {
    setLoading(true);

    apis
      .getTransactions({ ...query, limit })
      .then((result) => {
        setTotal(result.total);
        setList(result.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [query]);
  const navigateTo = useCallback(
    (param) => {
      window.scrollTop = 0;
      history.push(`/finances?${qs.stringify(param)}`);
    },
    [query]
  );
  const filterOnChange = useCallback((param) => {
    navigateTo({ ...param, offset: 0 });
  }, []);
  const handleTableChange = useCallback(
    (param) => {
      navigateTo({ ...query, ...param });
    },
    [query]
  );

  useEffect(() => {
    fetch();
  }, [location.search]);

  return (
    <div className={styles.wrap}>
      <Filters query={query} onChange={filterOnChange} />
      <PageableTable
        size="middle"
        className={styles.table}
        columns={columns}
        pagination={pagination}
        total={total}
        limit={limit}
        offset={+query.offset || 0}
        dataSource={list}
        onChange={handleTableChange}
        expandedRowRender={(record, index, indent, expanded) => {
          if (!expanded) {
            return null;
          }
          return (
            <div className={styles.orders}>
              {record.orders.map((order, idx) => {
                return (
                  <div
                    className={styles.orderItem}
                    key={order._id}
                    onClick={handleRowClick(order)}
                  >
                    <div className={styles.row}>
                      {idx + 1}. {order.order_no}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }}
        loading={loading}
      />
    </div>
  );
}
